/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Stacked BarChart in-group navigation utilities.
 * Ordering within a group is determined by each segment's stacked extreme coordinate:
 * - Positive segments: use the cumulative positive sum up to and including the segment (top edge).
 * - Negative segments: use the cumulative negative sum up to and including the segment (bottom edge).
 *
 * In-group navigation ignores stack-group boundaries and considers all series across stacks.
 * Cross-stack/group helpers are preserved for fallback navigation outside the current group.
 */

import { ChartSeriesData, Info } from '#utils/UNSAFE_visTypes/chart';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';

type Direction = 'prev' | 'next';

export function createStackedGroupNavigator<K, D extends BarItemData<K>>(
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  series: ChartSeriesData<K, D>[],
  numSeries: number,
  stackGroups: string[],
  getStackGroup: (series: ChartSeriesData<K, D>) => string | undefined,
  isItemNavigable: (seriesIndex: number, groupIndex: number) => boolean
) {
  const hasMultipleStacks = Array.isArray(stackGroups) && stackGroups.length > 1;

  const seriesToStackIndex: number[] = series
    ? series.map((s) => {
        const stackName = getStackGroup(s) ?? 'default';
        const idx = stackGroups.indexOf(stackName);
        return idx >= 0 ? idx : 0;
      })
    : new Array(numSeries).fill(0);

  const stackToSeries: number[][] = stackGroups.map(() => []);
  for (let i = 0; i < numSeries; i++) {
    const stIdx = seriesToStackIndex[i] ?? 0;
    if (stackToSeries[stIdx]) {
      stackToSeries[stIdx].push(i);
    }
  }

  const itemValue = (seriesIndex: number, groupIndex: number): number | undefined => {
    const it = getDataItem(seriesIndex, groupIndex);
    return it?.value;
  };

  const valueSign = (value: number | undefined): -1 | 0 | 1 => {
    if (value == null || value === 0) return 0;
    return value > 0 ? 1 : -1;
  };

  /**
   * Compute the stacked extreme coordinate for the given series at the given group:
   * - Positive values: cumulative positive sum up to and including this series within its stack (top edge).
   * - Negative values: cumulative negative sum up to and including this series within its stack (bottom edge).
   * - Zero/undefined: 0 (baseline).
   */
  const stackedEdge = (seriesIndex: number, groupIndex: number): number => {
    const v = itemValue(seriesIndex, groupIndex);
    const sgn = valueSign(v);

    const stIdx = seriesToStackIndex[seriesIndex];
    const seriesInStack = stackToSeries[stIdx] || [];

    let sum = 0;
    for (let i = 0; i < seriesInStack.length; i++) {
      const s = seriesInStack[i]!;
      const val = itemValue(s, groupIndex);

      if (sgn > 0) {
        // Accumulate only positive contributions
        if ((val ?? 0) > 0) sum += val as number;
      } else {
        // Accumulate only negative contributions
        if ((val ?? 0) < 0) sum += val as number;
      }

      if (s === seriesIndex) break;
    }
    return sum;
  };

  /**
   * Returns all series indices ordered bottom->top by their stacked extreme coordinate
   * for a given group. Ties are broken by series index to provide a stable order.
   */
  const orderedSeriesByStackedEdge = (groupIndex: number): number[] =>
    Array.from({ length: numSeries }, (_, s) => ({ s, pos: stackedEdge(s, groupIndex) }))
      .sort((a, b) => (a.pos === b.pos ? a.s - b.s : a.pos - b.pos))
      .map((e) => e.s);

  /**
   * In-group navigation across all stacks:
   * - "next": move toward the top (larger stackedEdge)
   * - "prev": move toward the bottom (smaller stackedEdge)
   */
  const moveWithinStack = (info: Info, direction: Direction): Info | undefined => {
    const group = info.groupIndex!;
    const ordered = orderedSeriesByStackedEdge(group);

    const currIdx = ordered.indexOf(info.seriesIndex!);
    if (currIdx === -1) return;

    const step = direction === 'next' ? 1 : -1;
    for (let k = currIdx + step; k >= 0 && k < ordered.length; k += step) {
      const nextSeries = ordered[k]!;
      if (isItemNavigable(nextSeries, group)) {
        return { seriesIndex: nextSeries, groupIndex: group };
      }
    }
    return;
  };

  return {
    hasMultipleStacks,
    moveWithinStack
  };
}
